/**
 * Business Profile API client (XC-ERP1-U3 §5.1; plan §6.3 C-12).
 *
 * Routed through the gateway (`/api/v1/business-profile` → entity_resolution).
 * The tenant is always the JWT's: no call carries a tenant id, and the operator
 * Pipeline tenant override is never attached. A 404 (route not deployed, or no
 * profile yet) is the empty state; a payload that fails the v1 contract is
 * refused whole, so the page never paints a value it cannot vouch for.
 */

import type { BusinessProfileReadModel } from '@/types/businessProfile';
import { BusinessProfileReadModelSchema } from '@/types/businessProfile/schema';
import { businessProfileApi } from './businessProfileClient';

export const BUSINESS_PROFILE_PATH = '/api/v1/business-profile';

/** The payload reached us but failed the v1 contract (`schema.ts`). */
export class BusinessProfileContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BusinessProfileContractError';
  }
}

export type BusinessProfileResult =
  | { kind: 'ready'; profile: BusinessProfileReadModel }
  | { kind: 'absent' };

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

export async function fetchBusinessProfile(): Promise<BusinessProfileResult> {
  let data: unknown;
  try {
    ({ data } = await businessProfileApi.get(BUSINESS_PROFILE_PATH));
  } catch (err) {
    if (statusOf(err) === 404) return { kind: 'absent' };
    throw err;
  }
  const parsed = BusinessProfileReadModelSchema.safeParse(data);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new BusinessProfileContractError(
      `business profile failed its contract at ${first?.path.join('.') || '(root)'}: ${first?.message ?? 'invalid'}`,
    );
  }
  return { kind: 'ready', profile: parsed.data };
}
